'use client';

import * as React from 'react';
import { DataTable } from '@/components/data-table/data-table';
import { useDataTable } from '@/hooks/use-data-table';
import { DataTableSortList } from '@/components/data-table/data-table-sort-list';

import { getFunnelsTableColumns } from './funnels-table-columns';
import { FunnelsTableActions } from './funnels-table-actions';
import { FunnelsTableActionBar } from './funnels-table-action-bar';
import { FunnelsTableSkeleton } from './FunnelsTableSkeleton';
import {
  FunnelBundleExportDialog,
  FunnelBundleImportDialog,
  type BundleIncludes,
  type ParsedBundleFile,
} from './funnel-bundle-dialogs';
import { MainTableState } from '@/lib/utils/table-state-utils';
import { Funnel, useExportFunnelBundle, useImportFunnelBundle } from '@/lib/api/funnels';
import { useStoreNavigate } from '@/hooks/use-app-navigate';
import toast from 'react-hot-toast';
import type { TableConfig } from '@/types/data-table';
import { DEFAULT_SKELETON_ROWS } from '@/config/data-table';
import { useTranslation } from 'react-i18next';

interface FunnelsTableProps {
  storeId: string;
  funnels: Funnel[];
  total: number;
  pageCount: number;
  isLoading: boolean;
  onTableStateChange: (state: MainTableState) => void;
  onRefetch: () => void;
  onCreateFunnel?: () => void;
  onEditFunnel?: (funnelId: string) => void;
  onDeleteFunnel?: (funnelId: string) => void;
  onSetAsDefault?: (funnelId: string) => void;
  config?: TableConfig;
}

/**
 * FunnelsTable component for displaying funnels with DataTable functionality
 */
export function FunnelsTable({
  storeId,
  funnels,
  total,
  pageCount,
  isLoading,
  onTableStateChange,
  onRefetch,
  onCreateFunnel,
  onEditFunnel,
  onDeleteFunnel,
  onSetAsDefault,
  config = {},
}: FunnelsTableProps) {
  const { t } = useTranslation('common')
  const { storeRoutes, navigate } = useStoreNavigate()
  const exportBundle = useExportFunnelBundle()
  const importBundle = useImportFunnelBundle()
  // Export: the row action only picks the funnel; the dialog picks what travels.
  const [exportFunnelId, setExportFunnelId] = React.useState<string | null>(null)
  // Import: the file is parsed up front so the dialog can show what it carries.
  const [pendingImport, setPendingImport] = React.useState<ParsedBundleFile | null>(null)
  // Job id of the running import; the dialog polls its step events with it.
  const [importJobId, setImportJobId] = React.useState<string | null>(null)

  const handleExportFunnel = React.useCallback((funnelId: string) => {
    setExportFunnelId(funnelId)
  }, [])

  const handleConfirmExport = React.useCallback(async (include: BundleIncludes) => {
    const funnelId = exportFunnelId
    if (!funnelId) return
    try {
      const bundle = await exportBundle.mutateAsync({ funnelId, storeId, include })
      const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${bundle.id || funnelId}.funnel.json`
      link.click()
      URL.revokeObjectURL(url)
      toast.success(t('funnelsTable.toasts.exported'))
      setExportFunnelId(null)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('funnelsTable.toasts.exportFailed'))
    }
  }, [exportBundle, exportFunnelId, storeId, t])

  const handleImportBundle = React.useCallback(async (file: File) => {
    try {
      const parsed = JSON.parse(await file.text())
      if (!parsed || typeof parsed !== 'object' || parsed.version !== 1) {
        toast.error(t('funnelsTable.toasts.importInvalid'))
        return
      }
      setPendingImport(parsed as ParsedBundleFile)
    } catch {
      toast.error(t('funnelsTable.toasts.importInvalid'))
    }
  }, [t])

  const handleConfirmImport = React.useCallback(async (include: BundleIncludes) => {
    const bundle = pendingImport
    if (!bundle) return
    const jobId = crypto.randomUUID().replace(/-/g, '')
    setImportJobId(jobId)
    try {
      const result = await importBundle.mutateAsync({ storeId, bundle: bundle as never, include, jobId })
      if (result.warnings?.length) {
        toast.success(t('funnelsTable.toasts.importedWithWarnings', { count: result.warnings.length }))
      } else {
        toast.success(t('funnelsTable.toasts.imported'))
      }
      setPendingImport(null)
      onRefetch()
      navigate(storeRoutes.STOREFRONT_EDITOR(result.funnelId))
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('funnelsTable.toasts.importFailed'))
    } finally {
      setImportJobId(null)
    }
  }, [importBundle, navigate, onRefetch, pendingImport, storeId, storeRoutes, t])

  const exportFunnelName = React.useMemo(
    () => funnels.find((funnel) => funnel.id === exportFunnelId)?.name,
    [exportFunnelId, funnels],
  )

  // Memoized columns to prevent unnecessary re-renders
  const columns = React.useMemo(
    () =>
      getFunnelsTableColumns({
        storeId,
        onEditFunnel,
        onDeleteFunnel,
        onSetAsDefault,
        onExportFunnel: handleExportFunnel,
        t,
      }),
    [handleExportFunnel, onDeleteFunnel, onEditFunnel, onSetAsDefault, storeId, t],
  )

    const { table } = useDataTable({
        data: funnels,
        columns,
        total: total || funnels.length,
        config: {
            ...config,
            clientMode: true,
            defaultSorting: [{ id: 'funnels.updatedAt', desc: true }],
        },
        onTableStateChange,
        getRowId: (row: Funnel) => row.id,
    })

  return (
    <>
      <DataTable
        table={table}
        actionBar={<FunnelsTableActionBar table={table} storeId={storeId} />}
        isLoading={isLoading}
        skeletonRows={<FunnelsTableSkeleton rows={config.skeletonRows || DEFAULT_SKELETON_ROWS} />}
      >
        <FunnelsTableActions
          table={table}
          onRefresh={onRefetch}
          onCreate={onCreateFunnel}
          onImport={handleImportBundle}
          isLoading={isLoading}
          isImporting={importBundle.isPending}
        />
        <DataTableSortList table={table} align="end" />
      </DataTable>
      <FunnelBundleExportDialog
        open={exportFunnelId !== null}
        funnelName={exportFunnelName}
        isPending={exportBundle.isPending}
        onClose={() => setExportFunnelId(null)}
        onConfirm={handleConfirmExport}
      />
      <FunnelBundleImportDialog
        bundle={pendingImport}
        isPending={importBundle.isPending}
        jobId={importJobId}
        onClose={() => setPendingImport(null)}
        onConfirm={handleConfirmImport}
      />
    </>
  )
}
